import CardBadge, { CARD_BADGE_VARIANT } from '@/atoms/CardBadge';
import CardWrapper from '@/atoms/CardWrapper';
import ToolTip from '@/atoms/ToolTip';
import { HYSTORY_TYPE_TO_ICON, HYSTORY_TYPE_TO_VARIANT } from '@/organisms/CardHistory/HistoryToIcon';
import { RARITY_API_REF } from '@/types/modifiers/references';
import clx from 'classnames';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';

import { Mapped, SIZE, VARIANT_COLOR_ENUM } from 'types/utils';
import { useCmsData } from 'utils/cms';
import Image from 'utils/Image';

const SIZE_TO_CLASSNAME: Mapped<SIZE, string> = {
  [SIZE.SM]: 'w-full max-w-[72px] max-h-[100px]',
  [SIZE.MD]: 'w-full max-w-[120px] max-h-[167px]',
  [SIZE.LG]: 'w-full max-w-[180px] max-h-[250px]',
};

const SIZE_TO_RADIUS: Mapped<SIZE, string> = {
  [SIZE.SM]: 'rounded-xs',
  [SIZE.MD]: 'rounded-sm',
  [SIZE.LG]: 'rounded-md',
};

type CardThumbProps = {
  size?: SIZE;
  imagePath?: string | null;
  className?: string;
  alt: string;
  inMyCollection?: number | string | null;
  inMySale?: number;
  isSuspended?: boolean;
  rarity?: keyof typeof RARITY_API_REF;
  foiled?: boolean;
  isExclusive?: boolean;
  numberCopyAvailable?: number;
};

const CardThumb = ({
  className,
  imagePath = '/card-back.jpg',
  size,
  alt,
  inMyCollection,
  inMySale,
  isSuspended,
  rarity,
  foiled,
  isExclusive,
  numberCopyAvailable,
}: CardThumbProps) => {
  const [image, setImage] = useState(imagePath);
  const { generalSettings } = useCmsData();
  const onError = useCallback((e: SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.onerror = null;
    setImage('https://www.altered.gg/card-back.jpg');
  }, []);

  useEffect(() => {
    setImage(imagePath);
  }, [imagePath]);

  return (
    <CardWrapper className={className} foiled={foiled} rarity={rarity} size={size}>
      <div className={clx('relative w-fit m-auto overflow-hidden', size ? SIZE_TO_RADIUS[size] : 'rounded-md')}>
        <Image
          alt={alt}
          className={size && SIZE_TO_CLASSNAME[size]}
          height={369}
          onError={onError}
          quality={75}
          src={image as string}
          width={264}
        />
      </div>
      <div className="absolute right-2 bottom-2 z-3 flex flex-col gap-1">
        {isSuspended && (
          <ToolTip value="suspended">
            <CardBadge
              icon={HYSTORY_TYPE_TO_ICON.SUSPENDED}
              variant={HYSTORY_TYPE_TO_VARIANT.SUSPENDED as keyof typeof CARD_BADGE_VARIANT}
            />
          </ToolTip>
        )}
        {typeof inMyCollection !== 'undefined' && inMyCollection !== null && !isExclusive && (
          <ToolTip value="copies-I-own">
            <CardBadge icon="fa-solid fa-book-sparkles" variant={VARIANT_COLOR_ENUM.NEUTRAL}>
              {inMyCollection}
            </CardBadge>
          </ToolTip>
        )}
        {typeof numberCopyAvailable !== 'undefined' && isExclusive && (
          <ToolTip value="exclusive-card">
            <CardBadge icon="fa-solid fa-stars" variant={VARIANT_COLOR_ENUM.NEUTRAL}>
              {`${numberCopyAvailable}/${inMyCollection}`}
            </CardBadge>
          </ToolTip>
        )}
        {generalSettings.marketplace && typeof inMySale !== 'undefined' && (
          <ToolTip value="copies-I-sell">
            <CardBadge icon="fa-solid fa-store">{inMySale}</CardBadge>
          </ToolTip>
        )}
      </div>
    </CardWrapper>
  );
};

export default CardThumb;
